import {generateClient} from "aws-amplify/api";
import {GraphQLQuery} from '@aws-amplify/api';
import {
    notifyError,
    notifySuccess
} from "../../../components/ServiceNotifications.tsx";
import {appSyncConfig} from "../config/stripe-api.ts";
import * as ProductsService from "../../products/services/ProductsService.ts";

const client = generateClient({
                                  endpoint: appSyncConfig.endpoint,
                                  region: appSyncConfig.region,
                                  authMode: 'userPool'
                              });

export type StripeProduct = {
    id: string;
    name: string;
    description?: string;
    active?: boolean;
    created?: string;
}

export type CreateStripeProduct = {
    createProduct: StripeProduct;
}

export type UpdateStripeProduct = {
    updateProduct: StripeProduct;
}

export type DeleteStripeProduct = {
    deleteProduct: string;
}

export type GetStripeProduct = {
    getProduct: StripeProduct;
}

const CREATE_STRIPE_PRODUCT = `
    mutation createProduct($input: CreateProductInput!) {
        createProduct(input: $input) {
            id
            name
            description
        }
    }
`;

const UPDATE_STRIPE_PRODUCT = `
    mutation updateProduct($input: UpdateProductInput!) {
        updateProduct(input: $input) {
            id
            name
            description
        }
    }
`;

const DELETE_STRIPE_PRODUCT = `
    mutation deleteProduct($input: DeleteProductInput!) {
        deleteProduct(input: $input)
    }
`;

const GET_STRIPE_PRODUCT = `
    query getProduct($id: ID!) {
        getProduct(id: $id) {
            id
            name
            description
            created
        }
    }
`;

export const createStripeProduct = async (localProductId: string, productData: {
    name: string;
    description?: string;
}) => {
    let stripeProductId: string | null = null;

    try {
        const response = await client.graphql<GraphQLQuery<CreateStripeProduct>>({
                                                                                     query: CREATE_STRIPE_PRODUCT,
                                                                                     variables: {input: productData},
                                                                                 });

        stripeProductId = response.data?.createProduct?.id || null;

        if (stripeProductId) {
            const localProduct = await ProductsService.fetch(localProductId);
            if (localProduct) {
                try {
                    await ProductsService.update(localProductId, {
                        ...localProduct,
                        billingProviderRecordId: stripeProductId
                    });
                } catch (updateError) {
                    // Rollback: Delete the Stripe product if local update fails
                    try {
                        await client.graphql<GraphQLQuery<DeleteStripeProduct>>({
                                                                                    query: DELETE_STRIPE_PRODUCT,
                                                                                    variables: {input: {id: stripeProductId}},
                                                                                });
                    } catch (rollbackError) {
                        console.error('Failed to rollback Stripe product creation:', rollbackError);
                    }
                    throw updateError;
                }
            }
        }

        notifySuccess('Stripe product created successfully');
        return response.data?.createProduct;
    } catch (e) {
        notifyError(e);
        throw e;
    }
};

export const updateStripeProduct = async (stripeProductId: string, localProductId: string, productData: {
    name: string;
    description?: string;
    active?: boolean;
}) => {
    try {
        const response = await client.graphql<GraphQLQuery<UpdateStripeProduct>>({
                                                                                     query: UPDATE_STRIPE_PRODUCT,
                                                                                     variables: {input: {id: stripeProductId, ...productData}},
                                                                                 });

        // Sync local product with Stripe product ID if missing
        const localProduct = await ProductsService.fetch(localProductId);
        if (localProduct && !localProduct.billingProviderRecordId) {
            await ProductsService.update(localProductId, {
                ...localProduct,
                billingProviderRecordId: stripeProductId
            });
        }

        notifySuccess('Stripe product updated successfully');
        return response.data?.updateProduct;
    } catch (e) {
        notifyError(e);
        throw e;
    }
};

export const deleteStripeProduct = async (stripeProductId: string, localProductId: string) => {
    // Update local record first to avoid orphaned Stripe products
    const localProduct = await ProductsService.fetch(localProductId);
    if (!localProduct) {
        throw new Error('Local product not found');
    }

    try {
        await ProductsService.update(localProductId, {
            ...localProduct,
            billingProviderRecordId: ''
        });

        const response = await client.graphql<GraphQLQuery<DeleteStripeProduct>>({
                                                                                     query: DELETE_STRIPE_PRODUCT,
                                                                                     variables: {input: {id: stripeProductId}},
                                                                                 });

        notifySuccess('Stripe product deleted successfully');
        return response.data?.deleteProduct;
    } catch (e) {
        // Rollback: Restore the Stripe product ID if Stripe deletion fails
        if (localProduct) {
            try {
                await ProductsService.update(localProductId, {
                    ...localProduct,
                    billingProviderRecordId: stripeProductId
                });
            } catch (rollbackError) {
                console.error('Failed to rollback local product update:', rollbackError);
            }
        }
        notifyError(e);
        throw e;
    }
};

export const getStripeProduct = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<GetStripeProduct>>({
                                                                                  query: GET_STRIPE_PRODUCT,
                                                                                  variables: {id},
                                                                              });
        return response.data?.getProduct;
    } catch (e) {
        notifyError(e);
        throw e;
    }
};